// reportUtils.js - Generador de reportes en TXT/JSON

export const generarReportePDF = (actividades, meses) => {
  // Generar reporte en texto plano (compatible con todos los navegadores)
  let contenido = '';
  
  // Header
  contenido += '╔════════════════════════════════════════════════════════════════════╗\n';
  contenido += '║  📚 LICEO ÁNGEL EMILIO CASADO - REPORTE DE ACTIVIDADES             ║\n';
  contenido += '║  Gestor Integral de Actividades Mensuales                          ║\n';
  contenido += '╚════════════════════════════════════════════════════════════════════╝\n\n';
  
  // Información general
  const ahora = new Date().toLocaleString();
  contenido += `Fecha de Generación: ${ahora}\n`;
  contenido += `Total de Actividades: ${actividades.length}\n`;
  contenido += `═══════════════════════════════════════════════════════════════════\n\n`;

  // Estadísticas por estado
  const estadisticas = {
    'sin iniciar': 0,
    'en proceso': 0,
    'finalizada': 0,
    'cancelada': 0,
    'programada': 0
  };

  actividades.forEach(a => {
    if (estadisticas.hasOwnProperty(a.estado)) {
      estadisticas[a.estado]++;
    }
  });

  contenido += '📊 ESTADÍSTICAS POR ESTADO:\n';
  contenido += '─────────────────────────────────────────────────────────────────\n';
  contenido += `❌ Sin Iniciar:    ${estadisticas['sin iniciar']} actividades\n`;
  contenido += `🔄 En Proceso:     ${estadisticas['en proceso']} actividades\n`;
  contenido += `✅ Finalizada:     ${estadisticas['finalizada']} actividades\n`;
  contenido += `⛔ Cancelada:      ${estadisticas['cancelada']} actividades\n`;
  contenido += `⏳ Programada:     ${estadisticas['programada']} actividades\n`;
  contenido += '═══════════════════════════════════════════════════════════════════\n\n';

  // Agrupar por mes
  const actividadesPorMes = {};
  actividades.forEach(a => {
    const mes = parseInt(a.fechaInicio.split('-')[1]);
    if (!actividadesPorMes[mes]) {
      actividadesPorMes[mes] = [];
    }
    actividadesPorMes[mes].push(a);
  });

  // Listar actividades por mes
  Object.keys(actividadesPorMes).forEach(mes => {
    const mesNum = parseInt(mes);
    contenido += `📅 ${meses[mesNum - 1].toUpperCase()} (${actividadesPorMes[mes].length} actividades)\n`;
    contenido += '─────────────────────────────────────────────────────────────────\n';

    actividadesPorMes[mes].forEach((a, index) => {
      contenido += `\n  ${index + 1}. ${a.nombre}\n`;
      contenido += `     Propósito: ${a.proposito}\n`;
      contenido += `     Responsable: ${a.responsable}\n`;
      contenido += `     Inicio: ${a.fechaInicio} | Cierre: ${a.fechaCierre}\n`;
      contenido += `     Estado: ${obtenerIconoEstado(a.estado)} ${a.estado}\n`;

      if (a.etiquetas && a.etiquetas.length > 0) {
        contenido += `     Categorías: ${a.etiquetas.join(', ')}\n`;
      }

      if (a.subtareas && a.subtareas.length > 0) {
        const completadas = a.subtareas.filter(s => s.completada).length;
        contenido += `     Subtareas: ${completadas}/${a.subtareas.length} completadas\n`;
      }

      if (a.comentarios && a.comentarios.length > 0) {
        contenido += `     Comentarios: ${a.comentarios.length}\n`;
      }
    });

    contenido += '\n═══════════════════════════════════════════════════════════════════\n\n';
  });

  // Estadísticas por responsable
  const responsables = {};
  actividades.forEach(a => {
    if (!responsables[a.responsable]) {
      responsables[a.responsable] = 0;
    }
    responsables[a.responsable]++;
  });

  contenido += '👤 ACTIVIDADES POR RESPONSABLE:\n';
  contenido += '─────────────────────────────────────────────────────────────────\n';
  Object.entries(responsables).forEach(([responsable, count]) => {
    contenido += `  • ${responsable}: ${count} actividades\n`;
  });

  contenido += '\n═══════════════════════════════════════════════════════════════════\n';
  contenido += 'Fin del Reporte\n';
  contenido += '═══════════════════════════════════════════════════════════════════\n';

  // Descargar como TXT
  const elemento = document.createElement('a');
  elemento.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(contenido);
  elemento.download = `reporte_actividades_${new Date().toISOString().split('T')[0]}.txt`;
  elemento.click();

  return contenido;
};

// Función auxiliar para obtener icono de estado
const obtenerIconoEstado = (estado) => {
  const iconos = {
    'sin iniciar': '❌',
    'en proceso': '🔄',
    'finalizada': '✅',
    'cancelada': '⛔',
    'programada': '⏳'
  };
  return iconos[estado] || '❓';
};

// Generar resumen en JSON
export const generarResumenJSON = (actividades) => {
  const resumen = {
    fecha_generacion: new Date().toISOString(),
    total_actividades: actividades.length,
    estadisticas: {
      sin_iniciar: actividades.filter(a => a.estado === 'sin iniciar').length,
      en_proceso: actividades.filter(a => a.estado === 'en proceso').length,
      finalizada: actividades.filter(a => a.estado === 'finalizada').length,
      cancelada: actividades.filter(a => a.estado === 'cancelada').length,
      programada: actividades.filter(a => a.estado === 'programada').length
    },
    actividades_detalladas: actividades.map(a => ({
      id: a.id,
      nombre: a.nombre,
      proposito: a.proposito,
      responsable: a.responsable,
      fecha_inicio: a.fechaInicio,
      fecha_cierre: a.fechaCierre,
      estado: a.estado,
      etiquetas: a.etiquetas || [],
      subtareas_total: a.subtareas ? a.subtareas.length : 0,
      subtareas_completadas: a.subtareas ? a.subtareas.filter(s => s.completada).length : 0,
      comentarios: a.comentarios ? a.comentarios.length : 0,
      recurrencia: a.recurrencia || 'ninguna'
    }))
  };

  return resumen;
};

// Estadísticas avanzadas
export const calcularEstadisticas = (actividades) => {
  const estadisticas = {
    total: actividades.length,
    por_estado: {},
    por_responsable: {},
    por_categoria: {},
    promedio_subtareas: 0,
    promedio_comentarios: 0,
    actividades_recurrentes: 0,
    progreso_general: 0
  };

  let totalSubtareas = 0;
  let totalComentarios = 0;
  let totalProgreso = 0;

  actividades.forEach(a => {
    // Por estado
    if (!estadisticas.por_estado[a.estado]) {
      estadisticas.por_estado[a.estado] = 0;
    }
    estadisticas.por_estado[a.estado]++;

    // Por responsable
    if (!estadisticas.por_responsable[a.responsable]) {
      estadisticas.por_responsable[a.responsable] = 0;
    }
    estadisticas.por_responsable[a.responsable]++;

    // Por categoría
    if (a.etiquetas) {
      a.etiquetas.forEach(etiqueta => {
        if (!estadisticas.por_categoria[etiqueta]) {
          estadisticas.por_categoria[etiqueta] = 0;
        }
        estadisticas.por_categoria[etiqueta]++;
      });
    }

    // Subtareas y comentarios
    totalSubtareas += a.subtareas ? a.subtareas.length : 0;
    totalComentarios += a.comentarios ? a.comentarios.length : 0;

    // Recurrentes
    if (a.recurrencia && a.recurrencia !== 'ninguna') {
      estadisticas.actividades_recurrentes++;
    }

    // Progreso general
    if (a.estado === 'finalizada') {
      totalProgreso += 100;
    } else if (a.estado === 'en proceso') {
      totalProgreso += 50;
    } else if (a.estado === 'sin iniciar') {
      totalProgreso += 0;
    } else if (a.estado === 'programada') {
      totalProgreso += 0;
    }
  });

  estadisticas.promedio_subtareas = actividades.length > 0 ? (totalSubtareas / actividades.length).toFixed(2) : 0;
  estadisticas.promedio_comentarios = actividades.length > 0 ? (totalComentarios / actividades.length).toFixed(2) : 0;
  estadisticas.progreso_general = actividades.length > 0 ? Math.round(totalProgreso / actividades.length) : 0;

  return estadisticas;
};

// Exportar a Excel XLSX
export const exportarExcel = (actividades) => {
  try {
    const XLSX = require('xlsx');
    
    // Preparar datos
    const data = actividades.map(a => ({
      'Nombre': a.nombre || '',
      'Propósito': a.proposito || '',
      'Responsable': a.responsable || '',
      'Inicio': a.fechaInicio || '',
      'Cierre': a.fechaCierre || '',
      'Estado': a.estado || '',
      'Etiquetas': a.etiquetas ? a.etiquetas.join(', ') : ''
    }));

    // Crear worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 30 },
      { wch: 40 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 }
    ];

    // Crear workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Actividades');

    // Descargar
    XLSX.writeFile(wb, `actividades-${new Date().toISOString().split('T')[0]}.xlsx`);
  } catch (error) {
    console.error('Error al exportar Excel:', error);
  }
};
